import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

import { Config } from '../../../environments/environment';
import { Authorization } from '../authorization/authorization.component';

/**
 * The Electronic Record and Signature Disclosure.
 *
 * ## Why this still talks to HttpClient directly
 *
 * The contract now exists — agree-api MR !22 — but on an unmerged branch, so the app's `api`
 * submodule still points at a spec without these paths and the generated client has no methods
 * for them. They are called by hand here, in ONE place, so the swap is a change to this file and
 * nothing else once that merge request lands and the pointer moves.
 *
 * What is deliberately NOT done: passing new arguments positionally to an existing generated
 * method ahead of regeneration. Those methods end `(..., observe = 'body', reportProgress?,
 * options?)`, so an extra positional argument is silently interpreted as `observe` — a corrupted
 * HTTP call rather than a compile error. That has bitten this codebase before, and it is why
 * this waits.
 *
 * ## The contract
 *
 * The version id that was rendered is the version id that is posted back, and it arrives in the
 * same response as the text it belongs to. Never "whichever is latest", and never a second fetch:
 * both lose the tie between the words on screen and the version recorded against the acceptance.
 *
 * IP address, user agent and timestamp are recorded server-side from the request. The client
 * does not supply them and must not — they are evidence about the request, not about the page.
 */

/**
 * What the server says about this signing request's disclosure requirement — including the text.
 *
 * One call, not two. The earlier shape asked whether consent was needed and then fetched the text
 * by id, which opens a window in which the text on screen and the version id recorded against the
 * acceptance could come from different reads. Since the whole point of this feature is evidentiary
 * — proving *which words* a signer agreed to — the text and its id have to arrive together.
 * Matches `disclosure_requirement` in agree.yaml.
 */
export interface DisclosureRequirement {
  /** Whether this signer must accept before signing. Frozen per job at send time. */
  required: boolean;
  /** Whether an acceptance row already exists — a reload must not re-gate them. */
  accepted: boolean;

  // The text fields below are absent when no version is in force for the brand, which is the
  // normal state for a deployment shipping with the feature dark.

  /** Id of the version in force. Sent back unchanged on acceptance. */
  versionId?: number;
  /** Human-readable label, e.g. "1.0". */
  version?: string;
  /** `tr` or `en`, resolved from the deployment's brand — never from Accept-Language. */
  language?: string;
  /** The disclosure text as Markdown, restricted to the CommonMark subset. */
  body?: string;
  /** SHA-256 of `body` as stored, so the payload is self-verifying. */
  bodySha256?: string;
  /**
   * The checkbox sentence. Versioned with the body and served rather than translated, because
   * it is part of the consent — the modal *heading* is chrome and does come from i18n.
   */
  consentText?: string;
}

export interface DisclosureAcceptance {
  disclosureVersionId: number;
  /** Whether the signer opened the modal. Recorded, never gated on. */
  modalOpened: boolean;
}

@Injectable()
export class DisclosureService {

  constructor(
    private http: HttpClient,
    private authorization: Authorization
  ) {}

  /**
   * Does this signing request need consent, and has it already been given?
   *
   * `code` is the recipient request code — the same one the signing page already holds — so a
   * guest with no account is served identically to a logged-in recipient.
   */
  getRequirement(code: string): Observable<DisclosureRequirement> {
    return this.http.get<DisclosureRequirement>(
      `${Config.WEBSERVICEURL}/recipient_requests/${encodeURIComponent(code)}/disclosure`,
      { headers: this.headers() }
    );
  }

  /** Same question for a multi-recipient bundle: required if ANY job in it requires it. */
  getBundleRequirement(multiCode: string): Observable<DisclosureRequirement> {
    return this.http.get<DisclosureRequirement>(
      `${Config.WEBSERVICEURL}/multirecipientrequests/${encodeURIComponent(multiCode)}/disclosure`,
      { headers: this.headers() }
    );
  }

  accept(code: string, acceptance: DisclosureAcceptance): Observable<any> {
    return this.http.post(
      `${Config.WEBSERVICEURL}/recipient_requests/${encodeURIComponent(code)}/disclosure/acceptance`,
      this.form(acceptance),
      { headers: this.formHeaders() }
    );
  }

  /** Bundle acceptance: one on-screen acceptance, fanned out server-side to each job. */
  acceptBundle(multiCode: string, acceptance: DisclosureAcceptance): Observable<any> {
    return this.http.post(
      `${Config.WEBSERVICEURL}/multirecipientrequests/${encodeURIComponent(multiCode)}/disclosure/acceptance`,
      this.form(acceptance),
      { headers: this.formHeaders() }
    );
  }

  /**
   * Form-encoded, not JSON. This API binds parameters with JAX-RS `@FormParam`, which is
   * populated only from `application/x-www-form-urlencoded` — a JSON or multipart body arrives
   * with every parameter null and the call quietly does nothing.
   */
  private form(acceptance: DisclosureAcceptance): string {
    const params = new URLSearchParams();
    params.set('disclosure_version_id', String(acceptance.disclosureVersionId));
    params.set('modal_opened', acceptance.modalOpened ? 'Y' : 'N');
    return params.toString();
  }

  private headers(): HttpHeaders {
    let headers = new HttpHeaders();
    const token = this.authorization.getAccessToken();
    if (token) {
      headers = headers.set('access_token', token);
    }
    return headers;
  }

  private formHeaders(): HttpHeaders {
    return this.headers().set('Content-Type', 'application/x-www-form-urlencoded');
  }
}
